/**
 * Admin Alerts Page
 *
 * Where admins define the rules that alert them when reports arrive: which
 * kind of report, how many within how many minutes (or every report), an
 * optional message pattern, and how to notify every platform admin (in-app,
 * email, or both). A rule that fires stays quiet for its cooldown, so a storm
 * of reports sends one alert.
 * Access: platform_admin.
 */

import { useState } from "react";
import {
  Bell,
  PencilSimple as Pencil,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  EmptyState,
  PageHeader,
  StatusPill,
} from "@charisol-spark/ui";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import {
  useAlertRules,
  useCreateAlertRule,
  useDeleteAlertRule,
  useUpdateAlertRule,
} from "@/hooks/useAdmin";
import type {
  AlertChannel,
  AlertRule,
  AlertRuleInput,
  AlertRuleKind,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

const KINDS: Array<{ value: AlertRuleKind; label: string }> = [
  { value: "any", label: "Any report" },
  { value: "error", label: "Errors" },
  { value: "bug", label: "Bugs" },
  { value: "feedback", label: "Feedback" },
  { value: "content", label: "Content (reported posts)" },
];

const CHANNELS: Array<{ value: AlertChannel; label: string }> = [
  { value: "in_app", label: "In-app notification" },
  { value: "email", label: "Email" },
  { value: "both", label: "In-app and email" },
];

const FIELD_CLASS =
  "h-9 w-full rounded-lg border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-50";

const LIST_COLUMNS = "minmax(0,1fr) 140px 90px 130px";

/** The rule in a sentence. */
function describeRule(
  rule: Pick<
    AlertRuleInput,
    | "kind"
    | "threshold"
    | "windowMinutes"
    | "messagePattern"
    | "cooldownMinutes"
  >
): string {
  const noun =
    rule.kind === "any"
      ? "report"
      : rule.kind === "feedback"
        ? "feedback report"
        : `${rule.kind} report`;
  const trigger =
    rule.threshold === 1
      ? `Every ${noun}`
      : `${rule.threshold} ${noun}s within ${rule.windowMinutes} minutes`;
  const pattern = rule.messagePattern
    ? ` containing "${rule.messagePattern}"`
    : "";
  return `${trigger}${pattern}. Quiet for ${rule.cooldownMinutes} minutes after an alert.`;
}

function formatDate(iso?: string) {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Rule form ──────────────────────────────────────────────

interface FormState {
  name: string;
  kind: AlertRuleKind;
  mode: "every" | "threshold";
  threshold: string;
  windowMinutes: string;
  messagePattern: string;
  channel: AlertChannel;
  cooldownMinutes: string;
  enabled: boolean;
}

function toForm(rule?: AlertRule): FormState {
  return {
    name: rule?.name ?? "",
    kind: rule?.kind ?? "error",
    mode: rule && rule.threshold > 1 ? "threshold" : "every",
    threshold: String(rule && rule.threshold > 1 ? rule.threshold : 5),
    windowMinutes: String(rule?.windowMinutes ?? 10),
    messagePattern: rule?.messagePattern ?? "",
    channel: rule?.channel ?? "both",
    cooldownMinutes: String(rule?.cooldownMinutes ?? 60),
    enabled: rule?.enabled ?? true,
  };
}

function whole(value: string): number {
  return /^\d+$/.test(value.trim()) ? Number(value) : NaN;
}

/** The rule the form describes, or what is wrong with it. */
function validateForm(
  form: FormState
): { ok: true; rule: AlertRuleInput } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  const threshold = form.mode === "every" ? 1 : whole(form.threshold);
  const windowMinutes = whole(form.windowMinutes);
  const cooldownMinutes = whole(form.cooldownMinutes);

  if (!form.name.trim()) errors.push("Give the rule a name.");
  if (form.mode === "threshold") {
    if (!(threshold >= 2 && threshold <= 1000)) {
      errors.push("The number of reports must be between 2 and 1000.");
    }
    if (!(windowMinutes >= 1 && windowMinutes <= 1440)) {
      errors.push("The window must be between 1 and 1440 minutes.");
    }
  }
  if (!(cooldownMinutes >= 1 && cooldownMinutes <= 10080)) {
    errors.push("The quiet period must be between 1 and 10080 minutes.");
  }
  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    rule: {
      name: form.name.trim(),
      enabled: form.enabled,
      kind: form.kind,
      threshold,
      windowMinutes: form.mode === "every" ? 15 : windowMinutes,
      ...(form.messagePattern.trim()
        ? { messagePattern: form.messagePattern.trim() }
        : {}),
      channel: form.channel,
      cooldownMinutes,
    },
  };
}

function RuleDialog({
  rule,
  open,
  onClose,
}: {
  rule?: AlertRule;
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateAlertRule();
  const update = useUpdateAlertRule();
  const [form, setForm] = useState<FormState>(() => toForm(rule));
  const [errors, setErrors] = useState<string[]>([]);
  const pending = create.isPending || update.isPending;
  const failure = create.error ?? update.error;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const result = validateForm(form);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors([]);
    if (rule) {
      // An emptied pattern clears it: the API reads "" as "remove".
      update.mutate(
        {
          ruleId: rule.ruleId,
          data: {
            ...result.rule,
            messagePattern: result.rule.messagePattern ?? "",
          },
        },
        { onSuccess: onClose }
      );
    } else {
      create.mutate(result.rule, { onSuccess: onClose });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <form onSubmit={submit} className="space-y-4" noValidate>
          <DialogHeader>
            <DialogTitle>
              {rule ? "Edit alert rule" : "New alert rule"}
            </DialogTitle>
            <DialogDescription>
              When a new report matches, every platform admin is notified.
            </DialogDescription>
          </DialogHeader>

          <div>
            <label
              htmlFor="rule-name"
              className="mb-1 block text-sm font-medium"
            >
              Name
            </label>
            <input
              id="rule-name"
              className={FIELD_CLASS}
              value={form.name}
              maxLength={100}
              placeholder="e.g. Errors spiking"
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="rule-kind"
                className="mb-1 block text-sm font-medium"
              >
                Reports of kind
              </label>
              <select
                id="rule-kind"
                className={FIELD_CLASS}
                value={form.kind}
                onChange={(e) => set("kind", e.target.value as AlertRuleKind)}
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="rule-channel"
                className="mb-1 block text-sm font-medium"
              >
                Notify admins by
              </label>
              <select
                id="rule-channel"
                className={FIELD_CLASS}
                value={form.channel}
                onChange={(e) => set("channel", e.target.value as AlertChannel)}
              >
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">Alert when</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="rule-mode"
                checked={form.mode === "every"}
                onChange={() => set("mode", "every")}
              />
              Every matching report arrives
            </label>
            <label className="flex flex-wrap items-center gap-2 text-sm">
              <input
                type="radio"
                name="rule-mode"
                checked={form.mode === "threshold"}
                onChange={() => set("mode", "threshold")}
              />
              <input
                aria-label="Number of reports"
                className={cn(FIELD_CLASS, "w-20")}
                inputMode="numeric"
                value={form.threshold}
                onFocus={() => set("mode", "threshold")}
                onChange={(e) => set("threshold", e.target.value)}
              />
              reports arrive within
              <input
                aria-label="Window in minutes"
                className={cn(FIELD_CLASS, "w-20")}
                inputMode="numeric"
                value={form.windowMinutes}
                onFocus={() => set("mode", "threshold")}
                onChange={(e) => set("windowMinutes", e.target.value)}
              />
              minutes
            </label>
          </fieldset>

          <div>
            <label
              htmlFor="rule-pattern"
              className="mb-1 block text-sm font-medium"
            >
              Message contains (optional)
            </label>
            <input
              id="rule-pattern"
              className={FIELD_CLASS}
              value={form.messagePattern}
              maxLength={200}
              placeholder="e.g. publish"
              onChange={(e) => set("messagePattern", e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Case does not matter. Only reports with this text are counted.
            </p>
          </div>

          <div className="flex items-end gap-4">
            <div>
              <label
                htmlFor="rule-cooldown"
                className="mb-1 block text-sm font-medium"
              >
                Quiet for (minutes) after an alert
              </label>
              <input
                id="rule-cooldown"
                className={cn(FIELD_CLASS, "w-28")}
                inputMode="numeric"
                value={form.cooldownMinutes}
                onChange={(e) => set("cooldownMinutes", e.target.value)}
              />
            </div>
            <label className="mb-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => set("enabled", e.target.checked)}
              />
              Enabled
            </label>
          </div>

          {errors.length > 0 && (
            <ul role="alert" className="space-y-0.5 text-sm text-destructive">
              {errors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
          {failure && (
            <p role="alert" className="text-sm text-destructive">
              {getErrorMessage(
                failure,
                "Could not save the rule. Check it and try again."
              )}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "Saving..." : rule ? "Save rule" : "Create rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ───────────────────────────────────────────────────

export function AdminAlertsPage() {
  const { data, isLoading, error } = useAlertRules();
  const update = useUpdateAlertRule();
  const remove = useDeleteAlertRule();
  const [editing, setEditing] = useState<AlertRule | "new" | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const rules = data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        variant="classic"
        title="Alerts"
        description="Rules that alert every platform admin when reports arrive."
        action={
          <Button size="sm" pill onClick={() => setEditing("new")}>
            <Plus className="mr-1.5 h-4 w-4" />
            New rule
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {getErrorMessage(
            error,
            "Could not load the alert rules. Refresh to retry."
          )}
        </Alert>
      ) : (
        <DataTable>
          <DataTableHead columns={LIST_COLUMNS}>
            <span>Rule</span>
            <span>Notifies by</span>
            <span>Status</span>
            <span />
          </DataTableHead>
          <DataTableBody>
            {rules.map((rule) => (
              <DataTableRow key={rule.ruleId} columns={LIST_COLUMNS}>
                <div className="min-w-0">
                  <p className="truncate font-medium">{rule.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {describeRule(rule)}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Last alerted: {formatDate(rule.lastAlertedAt)}
                  </p>
                </div>
                <div className="text-sm">
                  {CHANNELS.find((c) => c.value === rule.channel)?.label}
                </div>
                <div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={rule.enabled}
                    aria-label={`${rule.name} enabled`}
                    disabled={update.isPending}
                    onClick={() =>
                      update.mutate({
                        ruleId: rule.ruleId,
                        data: { enabled: !rule.enabled },
                      })
                    }
                    className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <StatusPill tone={rule.enabled ? "green" : "neutral"}>
                      {rule.enabled ? "On" : "Off"}
                    </StatusPill>
                  </button>
                </div>
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit ${rule.name}`}
                    onClick={() => setEditing(rule)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {confirmingId === rule.ruleId ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={remove.isPending}
                      onClick={() =>
                        remove.mutate(rule.ruleId, {
                          onSuccess: () => setConfirmingId(null),
                        })
                      }
                    >
                      Confirm delete
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Delete ${rule.name}`}
                      onClick={() => setConfirmingId(rule.ruleId)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </DataTableRow>
            ))}
          </DataTableBody>
          {rules.length === 0 && (
            <EmptyState
              icon={Bell}
              title="No alert rules yet."
              description="Add a rule to be told when errors, bugs or feedback arrive."
            />
          )}
        </DataTable>
      )}

      {(update.isError || remove.isError) && (
        <Alert variant="error">
          {getErrorMessage(
            update.error ?? remove.error,
            "Could not change the rule. Try again."
          )}
        </Alert>
      )}

      {editing !== null && (
        <RuleDialog
          key={editing === "new" ? "new" : editing.ruleId}
          rule={editing === "new" ? undefined : editing}
          open
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
